import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { createSelector } from 'reselect';
import { ReportRecord } from '../interfaces/report-record';
import { 
  api, 
  ChannelConnection, 
  ChannelStatsReportParams, 
  ChannelStatsReportResponse, 
  PublisherChannelConnectionParams, 
  PublisherChannelResponse,
  KeywordDistribution,
  IPDistribution,
  BrowserVersionDistribution,
  TimeOfDayDistributionResponse,
  CountryCodeDistribution,
  RefererDistribution,
  OfferDistribution,
  ApiResponse
} from './api';
import { AffiliateNetwork, Campaign, Channel, Offer, StatsApiResponse, UnconnectedTrafficReportCampaign, FeedMetadata } from './api-interfaces';
import { selectPerChannelStats } from './reports-service-reducer';
import { selectUnifiedOfferList } from './unified-selectors';
import { selectFeedMetadataMap } from './feed-metadata-service-reducer';
import { PlatformFilter } from './platformSlice';
import { RootState } from './store';

// Middleware to handle automatic retries for rate-limited requests
export const retryMiddleware = (store: any) => (next: any) => (action: any) => {
  const result = next(action);
  
  // Check if this is a fulfilled action for any of our channel stats endpoints
  const channelStatsEndpoints = [
    'getChannelKeywordDistribution',
    'getChannelIPDistribution', 
    'getChannelBrowserVersionDistribution',
    'getChannelTimeOfDayDistribution',
    'getChannelCountryCodeDistribution',
    'getChannelRefererDomainDistribution',
    'getChannelOfferDistribution',
    'getChannelTrafficReport',
    'getChannelFeedLogList'
  ];
  
  const actionType = action.type;
  const isChannelStatsEndpoint = channelStatsEndpoints.some(endpoint => 
    actionType.includes(endpoint) && actionType.includes('/fulfilled')
  );
  
  if (isChannelStatsEndpoint && action.payload?.shouldRetry) {
    const { campaignId, channelTag } = action.meta.arg.originalArgs;
    const endpointName = channelStatsEndpoints.find(endpoint => actionType.includes(endpoint));
    
    if (endpointName) {
      // Schedule retry after 5 seconds
      setTimeout(() => {
        const endpoint = (api.endpoints as any)[endpointName];
        if (endpoint) {
          store.dispatch(endpoint.initiate(
            { campaignId, channelTag }, 
            { forceRefetch: true }
          ));
        }
      }, 5000);
    }
  }
  
  return result;
};

// Helper function to create matchers for channel stats endpoints with retry logic
const createChannelStatsMatchers = <T>(
  builder: any,
  endpoint: any,
  stateKey: keyof Pick<StatsState, 'keywordDistribution' | 'ipDistribution' | 'browserVersionDistribution' | 'timeOfDayDistribution' | 'countryCodeDistribution' | 'refererDomainDistribution' | 'offerDistribution' | 'trafficReport' | 'feedLogList'>
) => {
  // Pending matcher
  builder.addMatcher(
    endpoint.matchPending,
    (state: StatsState, action: any) => {
      const { campaignId, channelTag } = action.meta.arg.originalArgs;
      const key: CampaignChannelKey = `${campaignId}-${channelTag}`;
      (state[stateKey] as any).isLoading[key] = true;
    }
  );

  // Fulfilled matcher with retry logic
  builder.addMatcher(
    endpoint.matchFulfilled,
    (state: StatsState, action: PayloadAction<ApiResponse<T>, string, { arg: { originalArgs: ChannelStatsReportParams } }>) => {
      const { campaignId, channelTag } = action.meta.arg.originalArgs;
      const key: CampaignChannelKey = `${campaignId}-${channelTag}`;
      const response = action.payload;
      (state[stateKey] as any).data[key] = response.data;
      (state[stateKey] as any).error[key] = response.error;
      (state[stateKey] as any).shouldRetry[key] = response.shouldRetry;
      (state[stateKey] as any).isLoading[key] = false;

      // Note: Retry logic will be handled by a separate middleware or effect
    }
  );

  // Rejected matcher
  builder.addMatcher(
    endpoint.matchRejected,
    (state: StatsState, action: any) => {
      const { campaignId, channelTag } = action.meta.arg.originalArgs;
      const key: CampaignChannelKey = `${campaignId}-${channelTag}`;
      (state[stateKey] as any).isLoading[key] = false;
      (state[stateKey] as any).error[key] = 'NETWORK_ERROR';
      (state[stateKey] as any).shouldRetry[key] = true;
    }
  );
};

export interface CountryOption {
    code: string;
    name: string;
    label: string;
}

export interface FeedFilterParams {
    searchQuery?: string;
    platformFilter?: PlatformFilter;
    liveStatusFilter?: 'all' | 'live' | 'not-live' | 'was-live';
    geoFilter?: CountryOption[];
    metadataFilters?: Partial<Record<keyof FeedMetadata, string[]>>;
}

export interface MissingStatsReport {
  _id: string;
  campaignId: string;
  channelTag: string;
  date: string;
  feedTag: string;
  offerId: string;
  trafficSourceId: string;
  visits: number;
  matchingReports: ReportRecord[]; // Define more specific type if possible
  offer: Offer;
  affiliateNetwork: AffiliateNetwork;
}

export interface MissingStatsReportApiResponse {
  missingStatsReports: MissingStatsReport[];
}


export interface DetailedDailyReport {
  date: string;
  publisher: string;
  feedProvider: string;
  channelTag: string;
  feedTag: string;
  coverage: string;
  rpm: string;
  monetizedRpm: string;
  totalRevenue: string;
  totalGrossRevenue: string;
  totalSearches: string;
  tags: string[];
  offerId: string;
}

export interface FinancialReport {
  date: string;
  total_searches: number;
  monetized_searches: number;
  clicks: number;
  revenue: number;
  gross_revenue: number;
  rpm: number;
  ctr: number;
}

export type OfferListReportRow = {
  offerId: string;
  prevDayVisits: number;
  visits: number;
  clickCapValue: number;
  offerName: string;
  offerNotes?: string;
  conversionCapValue: number;
  platform?: 'voluum' | 'redtrack'; // Platform field with default to voluum if missing
}

export interface FinancialReportData {
  [key: string]: FinancialReport;
}

export interface FinancialReportApiResponse {
  statsByDate: FinancialReportData;
  statsByDateAndChannel: FinancialReportData;
  statsByDateAndCampaign: FinancialReportData;
  revenueByFeedType: Record<string, number>;
}

// Define a new type for the combined key
type CampaignChannelKey = string;

// Define the state structure to store channel stats per campaign-channelTag
interface ChannelStatsByCampaignChannel {
  [key: CampaignChannelKey]: ChannelStatsReportResponse | null;
}

interface ChannelStatsReportState {
  data: ChannelStatsByCampaignChannel;
  isLoading: boolean;
}

// Individual component states
interface ChannelStatsComponentState<T> {
  data: { [key: CampaignChannelKey]: T | null };
  isLoading: { [key: CampaignChannelKey]: boolean };
  error: { [key: CampaignChannelKey]: string | null };
  shouldRetry: { [key: CampaignChannelKey]: boolean };
}

interface PublisherChannelConnectionState {
  [key: string]: PublisherChannelResponse | null;
}

export type OfferReportApiResponse = OfferListReportRow[];

interface StatsState {
  data: StatsApiResponse | null;
  detailedDailyReports: { [key: string]: DetailedDailyReport[] } | null;
  isLoading: boolean;
  financialReports: FinancialReportApiResponse | null;
  missingStatsReports: MissingStatsReportApiResponse | null;
  unconnectedTrafficReports: UnconnectedTrafficReportCampaign[] | null;
  channelStatsReportState: ChannelStatsReportState;
  publisherChannelConnectionState: PublisherChannelConnectionState;
  offerReport: OfferReportApiResponse | null;
  // Individual channel stats component states
  keywordDistribution: ChannelStatsComponentState<KeywordDistribution[]>;
  ipDistribution: ChannelStatsComponentState<IPDistribution[]>;
  browserVersionDistribution: ChannelStatsComponentState<BrowserVersionDistribution[]>;
  timeOfDayDistribution: ChannelStatsComponentState<TimeOfDayDistributionResponse>;
  countryCodeDistribution: ChannelStatsComponentState<CountryCodeDistribution[]>;
  refererDomainDistribution: ChannelStatsComponentState<RefererDistribution[]>;
  offerDistribution: ChannelStatsComponentState<OfferDistribution[]>;
  trafficReport: ChannelStatsComponentState<any[]>;
  feedLogList: ChannelStatsComponentState<any[]>;
}

const initialState: StatsState = {
  data: null,
  detailedDailyReports: null,
  isLoading: false,
  financialReports: null,
  missingStatsReports: null,
  unconnectedTrafficReports: null,
  offerReport: null,
  channelStatsReportState: {
    data: {},
    isLoading: false,
  },
  publisherChannelConnectionState: {},
  // Individual channel stats component initial states
  keywordDistribution: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  ipDistribution: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  browserVersionDistribution: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  timeOfDayDistribution: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  countryCodeDistribution: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  refererDomainDistribution: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  offerDistribution: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  trafficReport: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
  feedLogList: { data: {}, isLoading: {}, error: {}, shouldRetry: {} },
};

export const statsSlice = createSlice({
  name: 'stats',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Match the fulfilled action for the getStats query
    builder.addMatcher(
      api.endpoints.getStats.matchFulfilled,
      (state, action: PayloadAction<StatsApiResponse>) => {
        state.data = action.payload;
        state.isLoading = false;
      }
    );

    // Optionally, handle pending and rejected states
    builder.addMatcher(
      api.endpoints.getStats.matchPending,
      (state) => {
        state.isLoading = true;
      }
    );

    builder.addMatcher(
      api.endpoints.getStats.matchRejected,
      (state) => {
        state.data = null;
        state.isLoading = false;
      }
    );

    // Handle getDetailedStats mutation
    builder.addMatcher(
      api.endpoints.getDetailedStats.matchPending,
      (state, action) => {
        // Corrected type assertion
        const campaignId = ((action.meta.arg as unknown) as { originalArgs: { campaign: Campaign } }).originalArgs.campaign.campaignId;
        const campaign = state.data?.publisherList.find(c => c.campaignId === campaignId);
        if (campaign) {
          campaign.isLoading = true;
        }
      }
    );

    builder.addMatcher(
      api.endpoints.getDetailedStats.matchFulfilled,
      (state, action) => {
        // Corrected type assertion
        const campaignId = ((action.meta.arg as unknown) as { originalArgs: { campaign: Campaign } }).originalArgs.campaign.campaignId;
        const campaignIndex = state.data?.publisherList.findIndex(c => c.campaignId === campaignId);
        if (campaignIndex !== undefined && campaignIndex !== -1 && state.data) {
          state.data.publisherList[campaignIndex] = { ...state.data.publisherList[campaignIndex], ...action.payload, isLoading: false };
        }
      }
    );

    builder.addMatcher(
      api.endpoints.getDetailedStats.matchRejected,
      (state, action) => {
        // Corrected type assertion
        const campaignId = ((action.meta.arg as unknown) as { originalArgs: { campaign: Campaign } }).originalArgs.campaign.campaignId;
        const campaign = state.data?.publisherList.find(c => c.campaignId === campaignId);
        if (campaign) {
          campaign.isLoading = false;
        }
      }
    );

    // Handle getDetailedDailyReport query
    builder.addMatcher(
      api.endpoints.getDetailedDailyReport.matchFulfilled,
      (state, action: PayloadAction<DetailedDailyReport[]>) => {
        const reports = action.payload;
        if (reports.length > 0) {
          const date = reports[0].date;
          state.detailedDailyReports = {
            ...state.detailedDailyReports,
            [date]: reports.map((report, index) => ({
              id: index,
              ...report,
            })),
          };
        }
      }
    );

    // Handle getFinancialReport query
    builder.addMatcher(
      api.endpoints.getFinancialReport.matchFulfilled,
      (state, action: PayloadAction<FinancialReportApiResponse>) => {
        state.financialReports = action.payload;
      }
    );

    builder.addMatcher(
      api.endpoints.getFinancialReport.matchPending,
      (state) => {
        // Handle pending state if necessary
      }
    );

    builder.addMatcher(
      api.endpoints.getFinancialReport.matchRejected,
      (state) => {
        state.financialReports = null;
      }
    );

    builder.addMatcher(
      api.endpoints.getMissingStatsReport.matchFulfilled,
      (state, action: PayloadAction<MissingStatsReportApiResponse>) => {
        state.missingStatsReports = action.payload;
      }
    );

    builder.addMatcher(
      api.endpoints.getMissingStatsReport.matchPending,
      (state) => {
        // Handle pending state if necessary
      }
    );

    builder.addMatcher(
      api.endpoints.getMissingStatsReport.matchRejected,
      (state) => {
        state.missingStatsReports = null;
      }
    );

    builder.addMatcher(
      api.endpoints.getOfferReport.matchFulfilled,
      (state, action: PayloadAction<OfferReportApiResponse>) => {
        state.offerReport = action.payload;
      }
    );

    builder.addMatcher(
      api.endpoints.getOfferReport.matchPending,
      (state) => {
        // Handle pending state if necessary
      }
    );

    builder.addMatcher(
      api.endpoints.getOfferReport.matchRejected,
      (state) => {
        state.offerReport = null;
      }
    );

    // Handle getUnconnectedTrafficReport query
    builder.addMatcher(
      api.endpoints.getUnconnectedTrafficReport.matchFulfilled,
      (state, action: PayloadAction<UnconnectedTrafficReportCampaign[]>) => {
        state.unconnectedTrafficReports = action.payload;
      }
    );

    builder.addMatcher(
      api.endpoints.getUnconnectedTrafficReport.matchPending,
      (state) => {
        // Handle pending state if necessary
      }
    );

    builder.addMatcher(
      api.endpoints.getUnconnectedTrafficReport.matchRejected,
      (state) => {
        state.unconnectedTrafficReports = null;
      }
    );

    // Matchers for the new endpoints
    builder.addMatcher(
      api.endpoints.getChannelStatsReport.matchFulfilled,
      (state, action: PayloadAction<ChannelStatsReportResponse, string, { arg: { originalArgs: ChannelStatsReportParams } }>) => {
        const { campaignId, channelTag } = action.meta.arg.originalArgs;
        const key = `${campaignId}-${channelTag}`;
        state.channelStatsReportState.data[key] = action.payload;
        state.channelStatsReportState.isLoading = false;
      }
    ).addMatcher(
      api.endpoints.getChannelStatsReport.matchPending,
      (state) => {
        state.channelStatsReportState.isLoading = true;
      }
    ).addMatcher(
      api.endpoints.getChannelStatsReport.matchRejected,
      (state) => {
        state.channelStatsReportState.isLoading = false;
      }
    );

    // Add a matcher for the getChannelStatsReport query
    builder.addMatcher(
      api.endpoints.getCampaignChannelConnections.matchFulfilled,
      (state, action: PayloadAction<PublisherChannelResponse, string, { arg: { originalArgs: PublisherChannelConnectionParams } }>) => {
        const { campaignId } = action.meta.arg.originalArgs;
        const key = `${campaignId}`;
        state.publisherChannelConnectionState[key] = action.payload;
      }
    );

    // Individual channel stats component matchers using helper function
    createChannelStatsMatchers<KeywordDistribution[]>(builder, api.endpoints.getChannelKeywordDistribution, 'keywordDistribution');
    createChannelStatsMatchers<IPDistribution[]>(builder, api.endpoints.getChannelIPDistribution, 'ipDistribution');
    createChannelStatsMatchers<BrowserVersionDistribution[]>(builder, api.endpoints.getChannelBrowserVersionDistribution, 'browserVersionDistribution');
    createChannelStatsMatchers<TimeOfDayDistributionResponse>(builder, api.endpoints.getChannelTimeOfDayDistribution, 'timeOfDayDistribution');
    createChannelStatsMatchers<CountryCodeDistribution[]>(builder, api.endpoints.getChannelCountryCodeDistribution, 'countryCodeDistribution');
    createChannelStatsMatchers<RefererDistribution[]>(builder, api.endpoints.getChannelRefererDomainDistribution, 'refererDomainDistribution');
    createChannelStatsMatchers<OfferDistribution[]>(builder, api.endpoints.getChannelOfferDistribution, 'offerDistribution');
    createChannelStatsMatchers<any[]>(builder, api.endpoints.getChannelTrafficReport, 'trafficReport');
    createChannelStatsMatchers<any[]>(builder, api.endpoints.getChannelFeedLogList, 'feedLogList');

  },
});

// Selector to get stats from the state
const statsSelector = (state: RootState) => state.stats.data;

// Function to calculate total visits for a campaign
const getTotalVisits = (channelList: Channel[]) => {
  return channelList.reduce((total, channel) => total + (channel?.visits || 0), 0);
};

// Selector to get sorted stats
export const sortedStatsSelector = createSelector(
  [statsSelector],
  (stats) => {
    if (!stats) return [];

    // Clone the stats array to avoid mutating the original state
    const sortedStats = [...stats.publisherList]
    sortedStats.sort((a, b) => {
      return (b.channelList?.length ?? 0) - (a.channelList?.length ?? 0);
    });

    return sortedStats;
  }
);

// offers without publisher selector
export const offersWithoutPublisherSelector = createSelector(
  [statsSelector],
  (stats) => stats?.noPublisherOffers ?? []
);

// select valid offers
export const validOffersSelector = createSelector(
  [statsSelector],
  (stats) => {
    if (!stats) return [];

    return (stats.offerReport ?? []).filter(offer => offer.offerName.indexOf('Cap Trash') === -1);
  }
);

// select summary of all cap limits from offerList without offers that have trash in the name
export const totalCapSelector = createSelector(
  [validOffersSelector],
  (offerList) => {
    return offerList
      .reduce((total, offer) => total + (offer.clickCapValue ?? 0), 0);
  }
);

// select summary of all visits from offerList without offers that have trash in the name
export const totalVisitsSelector = createSelector(
  [validOffersSelector],
  (offerList) => {
    return offerList
      .reduce((total, offer) => total + (offer.visits ?? 0), 0);
  }
);

export const detailedDailyReportsSelector = createSelector(
  [(state: RootState) => state],
  (state) => state.stats.detailedDailyReports ?? {}
);

export const missingStatsReportsSelector = createSelector(
  [(state: RootState) => state],
  (state) => state.stats.missingStatsReports?.missingStatsReports ?? []
);

export const unconnectedTrafficReportsSelector = createSelector(
  [(state: RootState) => state],
  (state) => state.stats.unconnectedTrafficReports ?? []
);
// Export the reducer
export const statsReducer = statsSlice.reducer;

export const selectChannelStats = (state: RootState) => state.stats.channelStatsReportState.data;

export interface ChannelConnectionMap { [key: string]: ChannelConnection[] }
export interface ChannelDescriptor {
  channelTag: string;
  connectionList: ChannelConnection[];
}



export const selectCampaignChannelConnections = createSelector(
  [
    (state: RootState) => state.stats.publisherChannelConnectionState,
    (state: RootState, campaignId: string) => campaignId,
    selectUnifiedOfferList,
    (state: RootState, campaignId: string) => selectPerChannelStats(state, campaignId, {
      date: {
        $gte: moment.utc().subtract(30, 'days').toISOString(),
        $lt: moment.utc().toISOString()
      }
    })
  ],
  (publisherChannelConnections, campaignId, offerList, statsPerChannel): ChannelDescriptor[] => {
    const campaignConnections: ChannelConnection[] = publisherChannelConnections[campaignId] ?? [];

    // Aggregate by channelTag
    const channelConnectionMap: ChannelConnectionMap = {};
    campaignConnections.forEach(connection => {
      if (!channelConnectionMap[connection.channelTag]) {
        channelConnectionMap[connection.channelTag] = [];
      }
      // Now offerList is always an array with platform fields
      const offerName = offerList?.find(offer => offer.id === connection.offerId)?.name ?? 'Unknown';
      channelConnectionMap[connection.channelTag].push(({
        ...connection,
        offerName,
        stats: statsPerChannel[connection.channelTag] ?? {}
      }));
    });

    const channelDescriptorList = Object.keys(channelConnectionMap).map(channelTag => ({
      channelTag,
      connectionList: channelConnectionMap[channelTag],
      latestConnectionDate: channelConnectionMap[channelTag].reduce((latest, connection) => {
        return latest ? (latest.dateConnected > connection.dateConnected ? latest : connection) : connection;
      }).dateConnected
    }));
    // Sort by latest connection date
    channelDescriptorList.sort((a, b) => new Date(b.latestConnectionDate).getTime() - new Date(a.latestConnectionDate).getTime());

    return channelDescriptorList;
  }
);

export const selectProjectedRevenue = createSelector(
  [(state: RootState) => state.stats.financialReports?.statsByDate || {}],
  (financialReportData) => {
    const now = moment();
    const currentMonth = now.format('YYYY-MM');
    const daysInMonth = now.daysInMonth();
    const currentDay = now.date();
    const remainingDays = daysInMonth - currentDay;

    let totalGrossRevenue = 0;
    let highestDailyRevenue = 0;
    let daysWithData = 0;

    Object.entries(financialReportData).forEach(([date, report]) => {
      if (date.startsWith(currentMonth)) {
        totalGrossRevenue += report.gross_revenue;
        highestDailyRevenue = Math.max(highestDailyRevenue, report.gross_revenue);
        daysWithData++;
      }
    });

    // Calculate average daily revenue
    const averageDailyRevenue = daysWithData > 0 ? totalGrossRevenue / daysWithData : 0;

    // Project revenue to end of month using average daily revenue
    const projectedRevenue = totalGrossRevenue + (averageDailyRevenue * remainingDays);

    // Calculate highest potential revenue
    // Current revenue + (highest daily revenue * remaining days)
    const highestPotentialRevenue = totalGrossRevenue + (highestDailyRevenue * remainingDays);

    return {
      currentMonthToDate: totalGrossRevenue,
      projectedEndOfMonth: projectedRevenue,
      highestPotentialRevenue: highestPotentialRevenue,
    };
  }
);

// Selector for filtered feeds
export const selectFilteredFeeds = createSelector(
  [
    (state: RootState) => state.stats.offerReport || [],
    selectFeedMetadataMap,
    (state: RootState, filters: FeedFilterParams) => filters
  ],
  (data, feedMetadataMap, filters) => {
    const {
      searchQuery = '',
      platformFilter = 'both',
      liveStatusFilter = 'all',
      geoFilter = [],
      metadataFilters = {}
    } = filters;

    return data.filter((offer: OfferListReportRow) => {
      const metadata = feedMetadataMap.get(offer.offerId);
      const searchTerms = searchQuery?.trim().toLowerCase().split(/\s+/).filter(term => term.length > 0) || [];

      // Platform filtering - if no platform specified, assume voluum
      const offerPlatform = offer.platform || 'voluum';
      const matchesPlatform = platformFilter === 'both' ||
          platformFilter === offerPlatform;

      const matchesLiveStatus = liveStatusFilter === 'all' ||
          (liveStatusFilter === 'live' && offer.visits > 50) ||
          (liveStatusFilter === 'not-live' && offer.visits <= 50) ||
          (liveStatusFilter === 'was-live' && offer.prevDayVisits > 50 && offer.visits <= 50);

      const matchesGeo = geoFilter.length === 0 ||
          (metadata?.geo && geoFilter.some(g => metadata.geo?.includes(g?.code)));

      const matchesSearch = searchTerms.length === 0 || (() => {
          const fullQuery = searchQuery.trim().toLowerCase();
          
          // First, try to match the complete search query as a phrase
          if (fullQuery && (
              offer.offerName?.toLowerCase().includes(fullQuery) ||
              offer.offerNotes?.toLowerCase().includes(fullQuery)
          )) {
              return true;
          }
          
          // Then, check if all individual terms match
          return searchTerms.every(term => {
              const trimmedTerm = term.trim();
              if (!trimmedTerm) return true;
              
              return offer.offerName?.toLowerCase().includes(trimmedTerm) ||
                  (offer.offerNotes?.toLowerCase().includes(trimmedTerm) ?? false) ||
                  Object.entries(metadata || {}).some(([key, value]) => {
                      if (Array.isArray(value)) {
                          return value.some(item => item?.toLowerCase()?.includes(trimmedTerm));
                      }
                      if (typeof value === 'string') {
                          return value.toLowerCase().includes(trimmedTerm);
                      }
                      if (typeof value === 'object' && value !== null) {
                          return Object.values(value).some(subValue => {
                              if (Array.isArray(subValue)) {
                                  return subValue.some(item => item?.toLowerCase()?.includes(trimmedTerm));
                              }
                              if (typeof subValue === 'string') {
                                  return subValue.toLowerCase().includes(trimmedTerm);
                              }
                              return false;
                          });
                      }
                      return false;
                  });
          });
      })();

      const matchesFilters = Object.entries(metadataFilters).every(([key, filterValue]) => {
          if (filterValue === undefined || filterValue.length === 0) return true;

          const metadataValue = metadata?.[key as keyof FeedMetadata];

          const checkValue = (v: string) => {
              // Handle different metadata field types
              if (Array.isArray(metadataValue)) {
                  // Simple array fields like geo, browsers, devices
                  return metadataValue.includes(v);
              } else if (typeof metadataValue === 'object' && metadataValue !== null) {
                  // Handle feedType which has nested structure
                  if ('typeIn' in metadataValue || 'native' in metadataValue) {
                      const combinedValues = [
                          ...(metadataValue.typeIn || []),
                          ...(metadataValue.native || [])
                      ];
                      return combinedValues.includes(v);
                  }
              } else if (typeof metadataValue === 'string') {
                  // Handle single string values
                  return metadataValue === v;
              }
              return false;
          };

          // filterValue is always an array based on the interface
          return filterValue.some(checkValue); // Use 'some' instead of 'every' for OR logic within a filter
      });

      return matchesPlatform && matchesSearch && matchesFilters && matchesLiveStatus && matchesGeo;
    });
  }
);